export const ANALYTICS_CONSENT_CHOICES = Object.freeze({
  UNKNOWN: "unknown",
  GRANTED: "granted",
  DENIED: "denied",
});

// Public-web market and browser locale are user/device preferences, not trustworthy jurisdiction
// evidence. Keep this empty—and UNKNOWN consent-first—until a reviewed, trusted-edge country
// endpoint can supply short-lived policy evidence without exposing it to analytics.
export const DEFAULT_ON_ANALYTICS_COUNTRIES = Object.freeze([]);
export function normalizeAnalyticsConsentChoice(value) {
  const normalized = String(value || "")
    .trim()
    .toLowerCase();
  return normalized === ANALYTICS_CONSENT_CHOICES.GRANTED ||
    normalized === ANALYTICS_CONSENT_CHOICES.DENIED
    ? normalized
    : ANALYTICS_CONSENT_CHOICES.UNKNOWN;
}

export function normalizeAnalyticsCountry(value) {
  return String(value || "")
    .trim()
    .toUpperCase();
}

export function analyticsCountryFromLocaleTag(value) {
  const normalized = String(value || "")
    .trim()
    .replaceAll("_", "-");
  if (!normalized) return "";
  try {
    return normalizeAnalyticsCountry(new Intl.Locale(normalized).region);
  } catch {
    const parts = normalized.split("-");
    const region = parts.find((part, index) => index > 0 && /^[a-zA-Z]{2}$/.test(part));
    return normalizeAnalyticsCountry(region);
  }
}

export function resolveAnalyticsMarketContext({
  authenticatedAccount = false,
  firstRunCompleted = false,
  firstRunCountry,
  hasStoredPreferences = false,
  preferencesCountry,
} = {}) {
  const storedPreferencesAvailable = hasStoredPreferences === true;
  const country = normalizeAnalyticsCountry(
    authenticatedAccount === true
      ? storedPreferencesAvailable
        ? preferencesCountry
        : ""
      : firstRunCompleted === true && storedPreferencesAvailable
        ? preferencesCountry
        : firstRunCountry,
  );
  return Object.freeze({
    country,
    resolved:
      firstRunCompleted === true &&
      Boolean(country) &&
      (authenticatedAccount !== true || storedPreferencesAvailable),
  });
}

export function resolveAnalyticsConsent({
  marketCountry,
  deviceLocaleCountry,
  choice,
  preferencesLoaded = true,
  marketResolved = true,
  noticeAcknowledged = false,
  globalPrivacyControl = false,
}) {
  const normalizedChoice = normalizeAnalyticsConsentChoice(choice);
  // Neither the selected market nor navigator locale proves where a public-web visitor is.
  // A future trusted-edge integration must be reviewed before this can become true.
  const defaultOnRegion = false;
  const defaultOnForUnknownChoice = defaultOnRegion && noticeAcknowledged === true;
  const requiresPriorConsent = !defaultOnRegion;
  // First-run completion and a resolved market are hard prerequisites even
  // for an explicit grant. GPC is an effective opt-out and always wins.
  const analyticsStorageGranted =
    preferencesLoaded === true &&
    marketResolved === true &&
    globalPrivacyControl !== true &&
    normalizedChoice !== ANALYTICS_CONSENT_CHOICES.DENIED &&
    (normalizedChoice === ANALYTICS_CONSENT_CHOICES.GRANTED || defaultOnForUnknownChoice);

  return Object.freeze({
    choice: normalizedChoice,
    requiresPriorConsent,
    defaultOnForUnknownChoice,
    noticeAcknowledged: noticeAcknowledged === true,
    globalPrivacyControl: globalPrivacyControl === true,
    analyticsStorageGranted,
    // Analytics permission never implies advertising permission. Today's
    // Brief does not collect advertising identifiers on Android or web.
    adStorageGranted: false,
    adUserDataGranted: false,
    adPersonalizationGranted: false,
  });
}

export function runWithAnalyticsConsent(decision, action) {
  if (!decision?.analyticsStorageGranted || typeof action !== "function") return false;
  action();
  return true;
}
